import { Component, Input, Output, EventEmitter, OnInit, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  SlurryService,
  MixabilityDto,
  SlurryCapabilitiesDto,
  SlurryCapabilityItemDto
} from '../../../../services/slurry.service';

@Component({
  selector: 'app-slurry-mixability-tab',
  standalone: true,
  imports: [CommonModule, FormsModule, MatTooltipModule],
  templateUrl: './slurry-mixability-tab.component.html',
  styleUrls: ['./slurry-mixability-tab.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class SlurryMixabilityTabComponent implements OnInit {
  @Input() slurryId!: number;
  @Input() capabilities: SlurryCapabilitiesDto | null = null;
  @Input() isParentProjectCompleted = false;
  @Input() statusId = 1;

  /** Emitted after data load or save so parent can keep print-cache in sync. */
  @Output() saved = new EventEmitter<{
    mixabilityId: number | null;
    score: number | null;
    timeToWetSolids: number | null;
    notes: string;
  }>();

  private mixabilityId: number | null = null;
  mixScore: number | null = null;
  mixTimeToWetSolids: number | null = null;
  mixNotes = '';
  mixabilityMessage = '';
  blockedMixabilityActionMessage = '';
  mixabilityLoaded = false;
  mixabilityLoadFailed = false;

  readonly mixabilityScores = [
    { value: 0, label: '0 - No Vortex' },
    { value: 1, label: '1 - Tight Vortex' },
    { value: 2, label: '2 - Small Vortex (1/4 inch)' },
    { value: 3, label: '3 - Medium Vortex (1/2 inch)' },
    { value: 4, label: '4 - Large Vortex (3/4 inch or more)' }
  ];

  constructor(private readonly slurryService: SlurryService) {}

  ngOnInit(): void {
    this.load();
  }

  private load(): void {
    if (!this.slurryId || this.slurryId <= 0) {
      this.mixabilityLoaded = true;
      return;
    }
    this.slurryService.getMixability(this.slurryId).subscribe({
      next: (m) => {
        if (m) {
          this.mixabilityId = m.id ?? null;
          this.mixScore = m.score ?? null;
          this.mixTimeToWetSolids = m.timeToWetSolids ?? null;
          this.mixNotes = m.notes ?? '';
        } else {
          this.mixabilityId = null;
          this.mixScore = null;
          this.mixTimeToWetSolids = null;
          this.mixNotes = '';
        }
        this.mixabilityLoadFailed = false;
        this.mixabilityLoaded = true;
        this.saved.emit({ mixabilityId: this.mixabilityId, score: this.mixScore, timeToWetSolids: this.mixTimeToWetSolids, notes: this.mixNotes });
      },
      error: (err) => {
        this.mixabilityLoadFailed = true;
        this.mixabilityLoaded = true;
        console.error('Failed to load mixability:', err);
      }
    });
  }

  private getCapabilityAllowed(
    selector: (c: SlurryCapabilitiesDto) => SlurryCapabilityItemDto,
    fallback: boolean
  ): boolean {
    const item = this.capabilities ? selector(this.capabilities) : null;
    return item?.allowed ?? fallback;
  }

  private getCapabilityReason(
    selector: (c: SlurryCapabilitiesDto) => SlurryCapabilityItemDto
  ): string | null {
    const item = this.capabilities ? selector(this.capabilities) : null;
    if (!item || item.allowed || !item.reason) { return null; }
    return item.reason;
  }

  canUpdateMixability(): boolean {
    const fallback = !this.isParentProjectCompleted && this.statusId !== 5;
    return this.getCapabilityAllowed(c => c.updateMixability, fallback);
  }

  getUpdateMixabilityBlockedReason(): string {
    if (this.canUpdateMixability()) { return ''; }
    const metadataReason = this.getCapabilityReason(c => c.updateMixability);
    if (metadataReason) { return metadataReason; }
    if (this.isParentProjectCompleted) {
      return 'Mixability is read-only because the parent project is completed.';
    }
    return 'Mixability cannot be updated after the slurry reaches Test Completed.';
  }

  onBlockedActionClick(reason: string | null | undefined): void {
    this.blockedMixabilityActionMessage = reason ? reason : '';
  }

  shouldShowNotEnteredWarning(): boolean {
    return this.mixabilityLoaded && !this.mixabilityLoadFailed && this.slurryId > 0 && (this.mixabilityId ?? 0) <= 0;
  }

  getNotEnteredWarningMessage(): string {
    return 'Mixability is not entered yet.';
  }

  saveMixability(): void {
    if (!this.slurryId || this.slurryId <= 0 || !this.canUpdateMixability()) { return; }
    this.mixabilityMessage = '';
    const dto: MixabilityDto = {
      id: this.mixabilityId ?? undefined,
      slurryId: this.slurryId,
      score: this.mixScore,
      timeToWetSolids: this.mixTimeToWetSolids,
      notes: this.mixNotes || null
    };
    this.slurryService.updateMixability(this.slurryId, dto).subscribe({
      next: (result) => {
        this.mixabilityId = result.id ?? this.mixabilityId;
        this.mixScore = result.score ?? null;
        this.mixTimeToWetSolids = result.timeToWetSolids ?? null;
        this.mixNotes = result.notes ?? '';
        this.mixabilityMessage = 'Mixability saved.';
        this.saved.emit({ mixabilityId: this.mixabilityId, score: this.mixScore, timeToWetSolids: this.mixTimeToWetSolids, notes: this.mixNotes });
      },
      error: (err) => {
        console.error('Failed to save mixability:', err);
        alert('Failed to save mixability.');
      }
    });
  }
}
